import { useEffect } from "react";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { useForm } from "react-hook-form";

// Region is driven by the global sidebar toggle (URL segment) via
// useRegionFilter — only status is filtered locally here.
export interface PaymentPageFilters {
  status: string;
}

interface PaymentPageFilterProps {
  onApplyFilters: (filters: PaymentPageFilters) => void;
  currentFilters: PaymentPageFilters;
}

export default function PaymentPageFilter({
  onApplyFilters,
  currentFilters,
}: PaymentPageFilterProps) {
  const form = useForm<PaymentPageFilters>({
    defaultValues: currentFilters,
  });

  const handleStatusChange = (newStatus: string) => {
    onApplyFilters({ ...form.getValues(), status: newStatus });
  };

  const handleReset = () => {
    form.reset({ status: "ALL" });
    onApplyFilters({ status: "ALL" });
  };

  useEffect(() => {
    form.reset(currentFilters);
  }, [currentFilters, form]);

  const areFiltersApplied = currentFilters.status !== "ALL";

  return (
    <Form {...form}>
      <div className="flex flex-wrap items-center gap-4">
        <FormField
          control={form.control}
          name="status"
          render={({ field }) => (
            <FormItem>
              <Select
                value={field.value}
                onValueChange={(value) => {
                  field.onChange(value);
                  handleStatusChange(value);
                }}
              >
                <FormControl>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="ALL">All</SelectItem>
                  <SelectItem value="ACTIVE">Active</SelectItem>
                  <SelectItem value="INACTIVE">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </FormItem>
          )}
        />

        {areFiltersApplied && (
          <Button
            type="button"
            variant="outline"
            onClick={handleReset}
            className="flex items-center gap-1"
          >
            <X className="h-4 w-4" />
            Reset Filters
          </Button>
        )}
      </div>
    </Form>
  );
}
